import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, Instagram } from "lucide-react";
import { ASSETS, NAV, CONTACTS } from "@/data/content";
import { scrollToId } from "@/lib/scroll";
import { HeaderFrame } from "@/components/Ornament";

const INSTA_URL = CONTACTS.royal.socials[0].href;

export default function Header() {
  const [open, setOpen] = useState(false);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-3 sm:top-4 inset-x-0 z-50 px-3 sm:px-6"
      data-testid="site-header"
    >
      <div className="glass max-w-6xl mx-auto rounded-full pl-4 pr-2 sm:pl-5 sm:pr-2.5 py-2 flex items-center justify-between shadow-[0_10px_40px_rgba(0,0,0,0.5)] relative">
        <HeaderFrame />
        <button
          type="button"
          onClick={() => go("hero")}
          className="flex items-center gap-2.5 cursor-pointer"
          data-testid="header-brand"
        >
          <img
            src={ASSETS.parkLogo}
            alt="Park Kids Club"
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover bg-white ring-1 ring-emerald-300/40"
            decoding="async"
            data-testid="header-logo-park"
          />
          <img
            src={ASSETS.royalLogo}
            alt="Royal Kids Club"
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover ring-1 ring-blue-300/40 -ml-4"
            decoding="async"
            data-testid="header-logo-royal"
          />
          <span className="font-cinzel text-amber-200 text-xs sm:text-sm tracking-[0.18em] ml-1 hidden xs:inline sm:inline">
            PARK × ROYAL
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-7" data-testid="header-nav">
          {NAV.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => go(item.id)}
              className="text-sm text-slate-200/85 hover:text-amber-300 transition-colors duration-300 cursor-pointer"
              data-testid={`nav-${item.id}`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <a
          href={INSTA_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden md:flex lg:hidden items-center gap-2 rounded-full border border-amber-300/40 bg-amber-300/10 px-5 py-2 text-amber-200 hover:bg-amber-300/20 hover:border-amber-300 hover:text-amber-100 transition-all duration-300 cursor-pointer"
          data-testid="header-life-button"
        >
          <Instagram className="w-4 h-4" />
          <span className="font-cinzel text-xs tracking-[0.2em] lowercase">наше життя</span>
        </a>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => go("forma")}
            className="group flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#f3d079] to-[#d9a53a] text-[#061812] font-semibold text-xs sm:text-sm px-4 sm:px-6 py-2.5 sm:py-3 shadow-[0_0_24px_rgba(230,184,92,0.45)] hover:shadow-[0_0_36px_rgba(230,184,92,0.7)] transition-shadow duration-300 cursor-pointer"
            data-testid="header-cta-button"
          >
            ЗАПИСАТИСЯ
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <a
            href={INSTA_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="md:hidden text-amber-200 p-2 hover:text-amber-300 transition-colors cursor-pointer"
            data-testid="header-instagram-button"
            aria-label="Instagram"
          >
            <Instagram className="w-5 h-5" />
          </a>
          <button
            type="button"
            className="lg:hidden text-amber-200 p-2 cursor-pointer"
            onClick={() => setOpen((v) => !v)}
            data-testid="header-mobile-menu-button"
            aria-label="Меню"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden glass max-w-6xl mx-auto mt-2 rounded-3xl p-4 flex flex-col gap-1"
            data-testid="header-mobile-nav"
          >
            {NAV.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => go(item.id)}
                className="text-left text-slate-100 py-2.5 px-3 rounded-xl hover:bg-amber-400/10 hover:text-amber-300 transition-colors cursor-pointer"
                data-testid={`nav-mobile-${item.id}`}
              >
                {item.label}
              </button>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
